// Create a contact form using react-hook-form.

import { yupResolver } from '@hookform/resolvers/yup';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { shallowEqual, useDispatch } from 'react-redux';
import * as yup from 'yup';

import ControlledRadioGroup from '../../components/BasicUI/ControlledInputs/ControlledRadioGroup';
import ControlledSelect from '../../components/BasicUI/ControlledInputs/ControlledSelect';
import ControlledTextField from '../../components/BasicUI/ControlledInputs/ControlledTextField';
import {
  fetchContactBookings,
  fetchContactListings,
  toggleContactModalState,
} from '../../ducks/contact.duck';
import {
  CONTACT_CATEGORY_KEYS,
  CONTACT_ROLES,
  CONTACT_ROLE_KEYS,
  categoriesForRole,
  findCategory,
  isCategoryAllowedForRole,
} from '../../util/contactCategories';
import useAppSelector from '../../util/hooks/useAppSelector';
import useScreenSize from '../../util/hooks/useScreenSize';

// The free-text reference is a description of the case, not an identifier. The
// cap matches REFERENCE_MAX in server/api/contact/contactPayload.js, so what the
// customer is allowed to type is exactly what reaches the mail body uncut.
const REFERENCE_TEXT_MAX = 255;

const referenceTextFieldProps = { inputProps: { maxLength: REFERENCE_TEXT_MAX } };

const createLabels = (intl) => ({
  name: intl.formatMessage({ id: 'ContactForm.nameLabel' }),
  nameRequired: intl.formatMessage({ id: 'ContactForm.nameRequired' }),
  email: intl.formatMessage({ id: 'ContactForm.emailLabel' }),
  emailValid: intl.formatMessage({ id: 'ContactForm.emailValid' }),
  emailRequired: intl.formatMessage({ id: 'ContactForm.emailRequired' }),
  message: intl.formatMessage({ id: 'ContactForm.messageLabel' }),
  messageRequired: intl.formatMessage({ id: 'ContactForm.messageRequired' }),
  category: intl.formatMessage({ id: 'ContactForm.categoryLabel' }),
  categoryPlaceholder: intl.formatMessage({ id: 'ContactForm.categoryPlaceholder' }),
  categoryRequired: intl.formatMessage({ id: 'ContactForm.categoryRequired' }),
  role: intl.formatMessage({ id: 'ContactForm.roleLabel' }),
  roleRequired: intl.formatMessage({ id: 'ContactForm.roleRequired' }),
  referenceBooking: intl.formatMessage({ id: 'ContactForm.referenceBookingLabel' }),
  referenceListing: intl.formatMessage({ id: 'ContactForm.referenceListingLabel' }),
  referenceMaxLength: intl.formatMessage(
    { id: 'ContactForm.referenceMaxLength' },
    { maxLength: REFERENCE_TEXT_MAX },
  ),
  referenceOther: intl.formatMessage({ id: 'ContactForm.referenceOther' }),
  referenceLoading: intl.formatMessage({ id: 'ContactForm.referenceLoading' }),
  referenceListingsLoading: intl.formatMessage({ id: 'ContactForm.referenceListingsLoading' }),
  referenceBookingDescription: intl.formatMessage({
    id: 'ContactForm.referenceBookingDescriptionLabel',
  }),
  referenceBookingDescriptionHelper: intl.formatMessage({
    id: 'ContactForm.referenceBookingDescriptionHelper',
  }),
  referencePassDescription: intl.formatMessage({ id: 'ContactForm.referencePassDescriptionLabel' }),
  referencePassDescriptionHelper: intl.formatMessage({
    id: 'ContactForm.referencePassDescriptionHelper',
  }),
  referenceListingDescription: intl.formatMessage({
    id: 'ContactForm.referenceListingDescriptionLabel',
  }),
  referenceListingDescriptionHelper: intl.formatMessage({
    id: 'ContactForm.referenceListingDescriptionHelper',
  }),
});

/**
 * ContactForm is a form for contacting the site owner.
 * @param {object} props
 * @param {Function} props.onSubmit - function to call when the form is submitted
 */
const ContactFormComponent = ({ onSubmit }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { executeRecaptcha, container } = useGoogleReCaptcha();
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const currentUserHasListings = useAppSelector((state) => state.user.currentUserHasListings);

  const { sendContactFormInProgress, sendContactFormError } = useAppSelector(
    (state) => ({
      sendContactFormInProgress: state.contact.sendContactFormInProgress,
      sendContactFormError: state.contact.sendContactFormError,
    }),
    shallowEqual,
  );

  const {
    contactBookings,
    contactBookingsRole,
    contactBookingsInProgress,
    contactBookingsError,
    contactListings,
    contactListingsInProgress,
    contactListingsError,
  } = useAppSelector(
    (state) => ({
      contactBookings: state.contact.contactBookings,
      contactBookingsRole: state.contact.contactBookingsRole,
      contactBookingsInProgress: state.contact.contactBookingsInProgress,
      contactBookingsError: state.contact.contactBookingsError,
      contactListings: state.contact.contactListings,
      contactListingsInProgress: state.contact.contactListingsInProgress,
      contactListingsError: state.contact.contactListingsError,
    }),
    shallowEqual,
  );

  const { isMobile } = useScreenSize();

  const labels = useMemo(() => createLabels(intl), [intl]);

  const containerRef = useRef(null);
  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      return;
    }

    if (!containerRef.current) return;

    try {
      return await executeRecaptcha('newsletter');
    } catch (e) {
      // noop
    }
  }, [executeRecaptcha]);

  const firstName = currentUser?.attributes?.profile?.firstName;
  const lastName = currentUser?.attributes?.profile?.lastName;

  const roleOptions = useMemo(
    () =>
      CONTACT_ROLES.map(({ key }) => ({
        value: key,
        label: intl.formatMessage({ id: `ContactForm.role.${key}` }),
      })),
    [intl],
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      message: '',
      name: firstName && lastName ? `${firstName} ${lastName}` : '',
      email: currentUser?.attributes?.email || '',
      currentUserId: currentUser?.id.uuid || '',
      currentUrl: typeof window !== 'undefined' ? window.location.href : '',
      role: currentUser?.id ? (currentUserHasListings ? 'host' : 'camper') : 'prospect',
      category: '',
      reference: '',
      referenceLabel: '',
      referenceKind: '',
      passStatus: '',
      locale: intl.locale,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      platform: typeof window !== 'undefined' && window.cordova ? 'app' : 'web',
    },
    resolver: yupResolver(
      yup.object().shape({
        name: yup.string().required(labels.nameRequired),
        email: yup.string().email(labels.emailValid).required(labels.emailRequired),
        message: yup.string().required(labels.messageRequired),
        role: yup.string().oneOf(CONTACT_ROLE_KEYS, labels.roleRequired).required(labels.roleRequired),
        category: yup
          .string()
          .oneOf(CONTACT_CATEGORY_KEYS, labels.categoryRequired)
          .required(labels.categoryRequired),
        // Only the free-text variant is typed; a picked option writes a label
        // the app itself produced. The cap is the server's, mirrored here so an
        // over-long description is refused rather than silently clipped.
        referenceLabel: yup.string().max(REFERENCE_TEXT_MAX, labels.referenceMaxLength),
      }),
    ),
  });

  const role = useWatch({ control, name: 'role' });
  const category = useWatch({ control, name: 'category' });

  // The category dropdown only offers what the role can actually be asking
  // about — see `roles` in util/contactCategories.js.
  const categoryOptions = useMemo(
    () =>
      categoriesForRole(role).map(({ key }) => ({
        value: key,
        label: intl.formatMessage({ id: `ContactForm.category.${key}` }),
      })),
    [intl, role],
  );

  // Switching the role can pull the selected category out from under the
  // customer (a camper writing about his pass who then says he is a host). The
  // select would keep rendering the now-missing value as a blank row, so the
  // field goes back to the placeholder instead.
  useEffect(() => {
    if (category && !isCategoryAllowedForRole(category, role)) {
      setValue('category', '', { shouldValidate: false });
    }
  }, [category, role, setValue]);

  // A prospect has no account and therefore no case to point at; every other
  // combination is driven purely by the category.
  const referenceKind = useMemo(() => {
    if (role === 'prospect') return null;
    return findCategory(category)?.reference ?? null;
  }, [role, category]);

  // The customer's pass, as support needs to read it. There is no customer-
  // facing label: the pass is never rendered as a field, it is attached to the
  // mail — as `passStatus` for everyone who holds one, and additionally as the
  // reference when the request is about the pass itself.
  const passOption = useMemo(() => {
    const annualPass = currentUser?.attributes?.profile?.privateData?.annualPass;
    if (!annualPass?.active) return null;
    const validUntil = annualPass.validUntil ? ` aktiv bis ${annualPass.validUntil}` : ' aktiv';
    const twentyFive = annualPass.twentyFivePass ? ' · 25er-Pass' : '';
    return {
      id: annualPass.subscriptionId || 'annualPass',
      // Support reads one German inbox, so this label is German regardless of
      // the locale the customer wrote in.
      mailLabel: `Jahrespass${validUntil}${twentyFive}`,
    };
  }, [currentUser]);

  const referenceOptions = useMemo(() => {
    // Switching camper↔host fires a second lookup while the first is still in
    // flight; if that one resolves last the store holds the wrong side of the
    // marketplace. Only offer bookings that were fetched for the current role.
    if (referenceKind === 'booking') return contactBookingsRole === role ? contactBookings : [];
    // Only a host owns listings, and only he gets the picker — see
    // `referenceMode` below.
    if (referenceKind === 'listing') return contactListings;
    return [];
  }, [referenceKind, contactBookings, contactBookingsRole, contactListings, role]);

  // While the lookup is running the dropdown stays put with a loading
  // placeholder — swapping to the free-text field and back would throw away
  // whatever the customer typed in the meantime.
  // Only the two kinds that own a lookup can be pending; the pass is read
  // straight off `currentUser`.
  const referencePending =
    (referenceKind === 'booking' && contactBookingsInProgress) ||
    (referenceKind === 'listing' && contactListingsInProgress);
  // Each picker answers for its own lookup — and only for its own, so a failed
  // *booking* lookup left behind by an earlier category cannot decide what the
  // listing category renders.
  const referenceLookupError =
    (referenceKind === 'booking' && contactBookingsError) ||
    (referenceKind === 'listing' && contactListingsError);

  /**
   * What the reference field renders: a picker, a free-text description, or
   * nothing at all.
   *
   * - The pass needs no field: a customer holds at most one, so picking the
   *   pass category already says which one it is and the reference is attached
   *   silently below. Someone without a pass describes it instead.
   * - A camper never owns the listing he writes about, so he always describes
   *   it — there is nothing of his to pick from.
   * - A host writing about a booking either picks one of his sales or leaves
   *   the field out. Unlike the camper he has no id he could type: the booking
   *   number exists nowhere in his world, so a free-text field would only
   *   invite prose that repeats his message.
   */
  const referenceMode = useMemo(() => {
    if (!referenceKind) return null;
    if (referenceKind === 'pass') return passOption ? null : 'freeText';
    if (referenceKind === 'listing' && role !== 'host') return 'freeText';
    if (referencePending) return 'select';
    if (currentUser?.id && !referenceLookupError && referenceOptions.length > 0) return 'select';
    if (referenceKind === 'booking' && role === 'host') return null;
    return 'freeText';
  }, [
    referenceKind,
    role,
    passOption,
    referencePending,
    referenceLookupError,
    referenceOptions,
    currentUser?.id,
  ]);

  useEffect(() => {
    if (referenceKind === 'booking' && currentUser?.id) {
      dispatch(fetchContactBookings(role));
    }
  }, [referenceKind, role, currentUser?.id, dispatch]);

  // Gated on the host role, unlike the booking lookup above: a camper never
  // sees the listing picker, so fetching his (usually empty) own listings would
  // only buy a round-trip. No `role` dependency beyond that — `ownListings`
  // returns the same set whichever side of the marketplace he writes from.
  useEffect(() => {
    if (referenceKind === 'listing' && role === 'host' && currentUser?.id) {
      dispatch(fetchContactListings());
    }
  }, [referenceKind, role, currentUser?.id, dispatch]);

  // One effect owns the reference, so there is no ordering to get wrong: the
  // category/role deps drop a stale booking id — pointing support at the wrong
  // case, or at the wrong side of the marketplace — and the pass deps write the
  // pass back in the same run.
  useEffect(() => {
    if (referenceKind === 'pass' && passOption) {
      // Support reads one German inbox, so the mail carries the German label
      // while everything the customer sees stays in his locale.
      setValue('reference', passOption.id);
      setValue('referenceLabel', passOption.mailLabel);
      return;
    }
    setValue('reference', '');
    setValue('referenceLabel', '');
  }, [category, role, referenceKind, passOption, setValue]);

  useEffect(() => {
    // `free` is what the server expects for a reference the customer typed
    // rather than picked (see server/api/contact/contactPayload.js). Every kind
    // that can fall back to the description field reports it the same way.
    setValue(
      'referenceKind',
      referenceKind ? (referenceMode === 'freeText' ? 'free' : referenceKind) : '',
    );
  }, [referenceKind, referenceMode, setValue]);

  useEffect(() => {
    setValue('passStatus', passOption ? passOption.mailLabel : '');
  }, [passOption, setValue]);

  const handleReferenceChange = useCallback(
    (event) => {
      const selected = referenceOptions.find((option) => option.id === event.target.value);
      // ControlledSelect spreads `selectProps` after the field, so this handler
      // replaces react-hook-form's own onChange and has to write the value —
      // including the dirty/touched flags it would otherwise have set.
      setValue('reference', event.target.value, { shouldDirty: true, shouldTouch: true });
      setValue('referenceLabel', selected ? selected.label : '');
    },
    [referenceOptions, setValue],
  );

  const referenceSelectProps = useMemo(
    () => ({ displayEmpty: true, onChange: handleReferenceChange }),
    [handleReferenceChange],
  );

  const referenceSelectLabels = useMemo(
    () =>
      referenceKind === 'listing'
        ? { label: labels.referenceListing, loading: labels.referenceListingsLoading }
        : { label: labels.referenceBooking, loading: labels.referenceLoading },
    [referenceKind, labels],
  );

  // Neither a booking number nor a pass id exists in the customer's world — the
  // booking id is only ever a raw UUID in the address bar and the confirmation
  // mail carries no number at all. So the fallback asks for a description of the
  // case instead, which support can act on and which still reaches the subject
  // line. The wording follows the kind: asking "which booking?" of someone
  // writing about their pass reads as a non sequitur.
  const referenceDescription = useMemo(() => {
    if (referenceKind === 'pass') {
      return {
        label: labels.referencePassDescription,
        helper: labels.referencePassDescriptionHelper,
      };
    }
    if (referenceKind === 'listing') {
      return {
        label: labels.referenceListingDescription,
        helper: labels.referenceListingDescriptionHelper,
      };
    }
    return {
      label: labels.referenceBookingDescription,
      helper: labels.referenceBookingDescriptionHelper,
    };
  }, [referenceKind, labels]);

  const handleFormSubmit = async (data) => {
    const token = await handleReCaptchaVerify();
    if (!token) return;
    await onSubmit(data);
  };

  return (
    <Stack component="form" onSubmit={handleSubmit(handleFormSubmit)} spacing={2} width={isMobile ? '100%' : '30vw'}>
      <ControlledRadioGroup
        control={control}
        error={!!errors.role}
        helperText={errors.role ? errors.role.message : ''}
        label={labels.role}
        name="role"
        options={roleOptions}
        radioGroupProps={{ row: true }}
        required={true}
      />
      <ControlledSelect
        control={control}
        error={!!errors.category}
        fullWidth={true}
        helperText={errors.category ? errors.category.message : ''}
        label={labels.category}
        name="category"
        required={true}
        selectProps={{ displayEmpty: true }}
      >
        <MenuItem disabled value="">
          {labels.categoryPlaceholder}
        </MenuItem>
        {categoryOptions.map(({ value, label }) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </ControlledSelect>
      {referenceMode === 'freeText' ? (
        <ControlledTextField
          control={control}
          error={!!errors.referenceLabel}
          helperText={
            errors.referenceLabel ? errors.referenceLabel.message : referenceDescription.helper
          }
          label={referenceDescription.label}
          name="referenceLabel"
          textFieldProps={referenceTextFieldProps}
        />
      ) : null}
      {referenceMode === 'select' ? (
        <ControlledSelect
          control={control}
          fullWidth={true}
          label={referenceSelectLabels.label}
          name="reference"
          selectProps={referenceSelectProps}
        >
          <MenuItem value="">
            {referencePending ? referenceSelectLabels.loading : labels.referenceOther}
          </MenuItem>
          {referenceOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.label}
            </MenuItem>
          ))}
        </ControlledSelect>
      ) : null}
      <ControlledTextField
        control={control}
        error={!!errors.name}
        helperText={errors.name ? errors.name.message : ''}
        label={labels.name}
        name="name"
        required={true}
      />
      <ControlledTextField
        control={control}
        error={!!errors.email}
        helperText={errors.email ? errors.email.message : ''}
        label={labels.email}
        name="email"
        required={true}
        textFieldProps={{
          type: 'email',
        }}
      />
      <ControlledTextField
        control={control}
        error={!!errors.message}
        helperText={errors.message ? errors.message.message : ''}
        label={labels.message}
        name="message"
        required={true}
        textFieldProps={{
          multiline: true,
          rows: 4,
        }}
      />
      <input {...register('currentUserId')} type="hidden" />
      <input {...register('currentUrl')} type="hidden" />
      <input {...register('referenceKind')} type="hidden" />
      <input {...register('passStatus')} type="hidden" />
      <input {...register('locale')} type="hidden" />
      <input {...register('userAgent')} type="hidden" />
      <input {...register('platform')} type="hidden" />
      <Box id={container} ref={containerRef} />
      {sendContactFormError ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography>{intl.formatMessage({ id: 'ContactModal.error' })}</Typography>
        </Alert>
      ) : null}
      <Stack direction={isMobile ? 'column' : 'row'} justifyContent="flex-end" spacing={2}>
        <Button
          color="secondary"
          key="cancel"
          onClick={() => dispatch(toggleContactModalState(false))}
          variant="contained"
        >
          {intl.formatMessage({ id: 'ContactModal.cancel' })}
        </Button>
        <Button
          color="primary"
          disabled={sendContactFormInProgress}
          key="submit"
          loading={sendContactFormInProgress}
          type="submit"
          variant="contained"
        >
          {intl.formatMessage({ id: 'ContactModal.submit' })}
        </Button>
      </Stack>
    </Stack>
  );
};

ContactFormComponent.displayName = 'ContactForm';

const ContactForm = memo(ContactFormComponent);

export default ContactForm;
